export type SchrijvenTaskType = "email" | "wijkkrant" | "form" | "briefje";

export const SCHRIJVEN_TASK_TYPES: readonly SchrijvenTaskType[] = [
  "email",
  "wijkkrant",
  "form",
  "briefje",
] as const;

export function isSchrijvenTaskType(v: unknown): v is SchrijvenTaskType {
  return typeof v === "string" && (SCHRIJVEN_TASK_TYPES as readonly string[]).includes(v);
}

export interface SchrijvenScore {
  execution: number;
  grammar: number;
  spelling: number;
  clearness: number;
  vocabulary: number;
}

export const SCHRIJVEN_SCORE_MAX = 10;

export function totalScore(s: SchrijvenScore): number {
  return s.execution + s.grammar + s.spelling + s.clearness + s.vocabulary;
}

/**
 * Recipient relationship — drives the grader's register check (u-form vs
 * je-form) per task type. Null on form / wijkkrant variants where there's
 * no recipient.
 */
export type RecipientRelationship = "authority" | "peer" | "family";

export const RECIPIENT_RELATIONSHIPS: readonly RecipientRelationship[] = [
  "authority",
  "peer",
  "family",
] as const;

/**
 * Optional per-variant form layout. When present on a form-type variant, the
 * FormShell renders these sections literally — one row per field — instead of
 * the default one-input-per-bullet fallback. Mirrors the structure of real
 * DUO formulieren (e.g. Persoonsgegevens block + open-ended question
 * sections).
 */
export type SchrijvenFormFieldKind =
  | "line"
  | "text-short"
  | "area"
  | "text-block"
  | "radio"
  | "enum";

export interface SchrijvenFormField {
  /** Field label shown next to the input. Empty string for unlabeled
   *  open-ended fields (section title acts as the label). */
  label: string;
  /**
   * Render kind:
   * - "line"       — full-width single-line input (default for names, addresses).
   * - "text-short" — narrow single-line input (dates, postcodes, phone numbers).
   * - "area"       — multi-line textarea.
   * - "text-block" — multi-line textarea with an optional minimum-sentences hint.
   * - "radio"      — inline yes/no (or arbitrary option set) selector.
   * - "enum"       — native <select> dropdown.
   */
  kind: SchrijvenFormFieldKind;
  /**
   * For "radio" / "enum" kinds — the available options. Single value goes
   * into the serialised form output as `Label: <option>`.
   */
  options?: string[];
  /**
   * For "text-block" — a minimum-sentence hint shown next to the field
   * (e.g. "Schrijf minimaal 3 zinnen"). Grader can use this as a soft floor.
   */
  minSentences?: number;
  /**
   * When set, this field only renders when the named parent field has the
   * specified value. Used for conditional sub-fields (Ja → follow-up
   * question, Nee → hidden). Parent reference is "sectionTitle.fieldLabel".
   */
  showWhen?: { field: string; equals: string };
}

export interface SchrijvenFormSection {
  title: string;
  fields: SchrijvenFormField[];
}

export interface SchrijvenFormSchema {
  sections: SchrijvenFormSection[];
}

export interface SchrijvenVariantContext {
  id: string;
  taskType: SchrijvenTaskType;
  scenario: string;
  bullets: string[];
  recipient: string | null;
  greeting: string | null;
  closing: string | null;
  imageUrls: { url: string; captionWord: string }[];
  /** Present only when the operator authored a specific form layout for
   *  this variant. Always absent for non-form task types. */
  formSchema: SchrijvenFormSchema | null;
  /** Drives register check (u-form vs je-form). Null on form/wijkkrant. */
  recipientRelationship: RecipientRelationship | null;
  /** When set, student must invent N specific items (DUO "U moet zelf X
   *  dingen verzinnen" twist). Grader counts. Null when not applicable. */
  inventCount: number | null;
}

export const SCHRIJVEN_TASK_LABELS: Record<SchrijvenTaskType, string> = {
  email: "E-mail",
  wijkkrant: "Wijkkrant",
  form: "Formulier",
  briefje: "Briefje",
};
